import { useEffect, useState } from "react";
import { Lock, LogOut, Mail, Phone, Trash2, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, adminHeaders, getAdminPw, setAdminPw, clearAdminPw } from "../lib/api";
import { Input } from "../components/ui/input";

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      if (data?.success) {
        setAdminPw(password);
        toast.success("Accesso effettuato");
        onLogin();
      }
    } catch (err) {
      toast.error("Password non valida");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F3] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border border-neutral-200 p-10 rounded-3xl shadow-lg"
      >
        <div className="w-12 h-12 bg-[#0B1E3F] text-white flex items-center justify-center mb-8 rounded-xl">
          <Lock className="w-5 h-5" />
        </div>
        <div className="overline mb-3">/ Area riservata</div>
        <h1 className="hero-title text-3xl md:text-4xl mb-8">Accesso<br />amministratore</h1>
        <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
          Password
        </label>
        <Input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="rounded-xl border border-neutral-300 h-12 px-4 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base mb-8"
          placeholder="••••••••"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#0B1E3F] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-6 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          Accedi
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminPw());
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        api.get("/admin/contatti", { headers: adminHeaders() }),
        api.get("/admin/stats", { headers: adminHeaders() }),
      ]);
      setSubmissions(subsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminPw();
        setAuthed(false);
        toast.error("Sessione scaduta, effettua nuovamente l'accesso");
      } else {
        toast.error("Errore nel caricamento dati");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contatti/${id}/read`, {}, { headers: adminHeaders() });
      toast.success("Segnato come letto");
      load();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await api.delete(`/admin/contatti/${id}`, { headers: adminHeaders() });
      toast.success("Richiesta eliminata");
      load();
    } catch {
      toast.error("Errore in eliminazione");
    }
  };

  const logout = () => {
    clearAdminPw();
    setAuthed(false);
    toast.success("Disconnesso");
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <div className="border-b border-neutral-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1D4ED8]">
              / Area riservata
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl">
              Richieste di contatto
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-neutral-300 hover:bg-[#0B1E3F] hover:text-white hover:border-[#0B1E3F] px-4 py-2 rounded-full font-mono text-[11px] tracking-[0.15em] uppercase transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-neutral-300 hover:bg-[#0B1E3F] hover:text-white hover:border-[#0B1E3F] px-4 py-2 rounded-full font-mono text-[11px] tracking-[0.15em] uppercase transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-10">
          <div className="bg-white p-6">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">Totale</div>
            <div className="stat-number text-[#0B1E3F]" data-testid="admin-stat-total">{stats.total}</div>
          </div>
          <div className="bg-white p-6">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">Non lette</div>
            <div className="stat-number text-[#1D4ED8]" data-testid="admin-stat-unread">{stats.unread}</div>
          </div>
          <div className="bg-white p-6">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">Lette</div>
            <div className="stat-number text-neutral-400">{stats.total - stats.unread}</div>
          </div>
          <div className="bg-white p-6">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-2">Ultima</div>
            <div className="font-display font-bold text-xl mt-4">
              {submissions[0]
                ? new Date(submissions[0].created_at).toLocaleDateString("it-IT")
                : "—"}
            </div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 overflow-x-auto rounded-2xl">
          <table className="admin-table w-full min-w-[800px]" data-testid="admin-submissions-table">
            <thead>
              <tr>
                <th>Stato</th>
                <th>Nome</th>
                <th>Contatto</th>
                <th>Oggetto / Messaggio</th>
                <th>Data</th>
                <th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-neutral-500">
                    Nessuna richiesta ricevuta.
                  </td>
                </tr>
              )}
              {submissions.map((s) => (
                <tr key={s.id} className={!s.letto ? "unread" : ""} data-testid={`admin-row-${s.id}`}>
                  <td>
                    <span
                      className={`inline-block font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${
                        s.letto
                          ? "border-neutral-300 text-neutral-500"
                          : "border-[#1D4ED8] text-[#1D4ED8] font-bold"
                      }`}
                    >
                      {s.letto ? "Letto" : "Nuovo"}
                    </span>
                  </td>
                  <td className="font-display font-bold">{s.nome}</td>
                  <td>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-3.5 h-3.5 text-neutral-400" />
                      <a href={`mailto:${s.email}`} className="hover:text-[#1D4ED8] break-all">{s.email}</a>
                    </div>
                    {s.telefono && (
                      <div className="flex items-center gap-2 text-sm mt-1 text-neutral-600">
                        <Phone className="w-3.5 h-3.5 text-neutral-400" />
                        <a href={`tel:${s.telefono}`} className="hover:text-[#1D4ED8]">{s.telefono}</a>
                      </div>
                    )}
                  </td>
                  <td className="max-w-md">
                    <div className="font-display font-bold mb-1">{s.oggetto}</div>
                    <div className="text-sm text-neutral-600 line-clamp-3">{s.messaggio}</div>
                  </td>
                  <td className="font-mono text-xs text-neutral-500 whitespace-nowrap">
                    {new Date(s.created_at).toLocaleString("it-IT", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </td>
                  <td>
                    <div className="flex gap-2 justify-end">
                      {!s.letto && (
                        <button
                          onClick={() => markRead(s.id)}
                          data-testid={`admin-mark-read-${s.id}`}
                          className="w-9 h-9 border border-neutral-300 hover:bg-[#1D4ED8] hover:border-[#1D4ED8] hover:text-white flex items-center justify-center transition-colors rounded-lg"
                          title="Segna come letto"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => remove(s.id)}
                        data-testid={`admin-delete-${s.id}`}
                        className="w-9 h-9 border border-neutral-300 hover:bg-[#0B1E3F] hover:border-[#0B1E3F] hover:text-white flex items-center justify-center transition-colors rounded-lg"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
